import React, { useEffect, useState } from "react"; // eslint-disable-next-line no-restricted-imports
import { useSelector } from "react-redux";
import {
  Col,
  Row,
  UiBox,
  UiCenter,
  UiFlex,
  UiImage,
  UiPrimaryButton,
  UiText,
  useBreakpointValue,
} from "~/components/elements";
import { DashboardDropzone } from "~/components/shared/DashboardDropzone";
import { MAX_FILES_UPLOAD } from "~/constants/dashboard";
import { useAppDispatch, useAppSelector, useRequestState } from "~/hooks";
import { EDashboardStep, setActiveStep, setDashboardProgress } from "~/redux/dashboards/slice";
import { confirmJob, uploadPhotoMisc } from "~/redux/dashboards/thunk";
import { RootState } from "~/redux/root-reducer";
import { DashboardMainBox } from "~/components/shared/DashboardMainBox";
import { PageLayout } from "~/components/layouts/PageLayout";
import { DashboardStepper } from "~/components/shared/DashboardStepper";
import { useHistory } from "react-router-dom";
import images from "~/assets/img";
import { PAGES } from "~/constants";
import { requestOK } from "redux-thunk-kit";
import DisclaimerModal from "~/pages/result-detail/components/DisclaimerModal";
import { removeUserDashboardStorage } from "~/utils/helpers";
import { PhotoshootType } from "~/types/models/dashboard";

function PlaceholderUpload() {
  return (
    <>
      <UiFlex flexDirection={{ base: "column", lg: "row" }} align="center" gap={{ base: 4, lg: 3 }}>
        <UiImage src={images.UploadPlaceholder} width="6" height="6" objectFit="cover" />
        <UiText textAlign="center" vr="title2">
          Click to select photos or drag & drop them here
        </UiText>
      </UiFlex>
      <UiText textAlign="center" vr="body3" mt={1} color="neutral.gray400">
        Supports PNG, JPG, and HEIC image formats up to 14mb
      </UiText>
    </>
  );
}

export function UploadPhotoPage() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { screenIndex } = useSelector((state: RootState) => state.dashboard.dashboardProgress);
  const photoshootType: PhotoshootType = useSelector((state: RootState) => state.dashboard.selectedType);
  const confirmKeyFeature = useSelector((state: RootState) => state.dashboard.confirmKeyFeature);
  const confirmKeyFeature2 = useSelector((state: RootState) => state.dashboard.confirmKeyFeature2);

  // Separate upload states for each person
  const [uploadedFilesPerson1, setUploadedFilesPerson1] = useState<any[]>([]);
  const [uploadedFilesPerson2, setUploadedFilesPerson2] = useState<any[]>([]);

  // Current step for two-person flow (1 = person 1 upload, 2 = person 2 upload)
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  const { loading } = useRequestState(uploadPhotoMisc);
  const { loading: confirmLoading } = useRequestState(confirmJob);
  const vrTitle = useBreakpointValue<any>({ base: "headline4", md: "headline2" });
  const vrCaption = useBreakpointValue<any>({ base: "body2", md: "body1" });
  const styleData = useSelector((state: RootState) => state.dashboard.styleData);
  const headshotStyleSelected = useAppSelector(state => state.dashboard.headshotStyleSelected);
  const surveyIds = useSelector((state: RootState) => state.dashboard.confirmSurvey);
  const jobId = useSelector((state: RootState) => state.dashboard.jobId);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  const { invalid_images: invalidImages, valid_images: validImages } =
    useSelector((state: RootState) => state.dashboard.verifyResult) || {};

  useEffect(() => {
    dispatch(setActiveStep({ step: 3 }));
  }, [dispatch]);

  useEffect(() => {
    if (validImages && validImages.length > -1) {
      // For backwards compatibility with single person flow
      if (photoshootType === "one_person") {
        setUploadedFilesPerson1(validImages);
      } else {
        // For two-person flow, split the images based on person data
        setUploadedFilesPerson1(validImages.filter(img => img.type === "first_person" || !img.type));
        setUploadedFilesPerson2(validImages.filter(img => img.type === "second_person"));
      }
    }
  }, [validImages, photoshootType]);

  const onClickBack = async () => {
    // Handle back navigation for two-person flow
    if (photoshootType === "two_person" && currentStep === 2) {
      // When currently uploading for Person 2: back returns to Person 1
      setCurrentStep(1);
      return;
    }
    // When currently uploading for Person 1: back goes to previous page
    history.push(PAGES.KEY_FEATURES);
  };

  const onClickNext = async () => {
    // Handle next navigation for two-person flow
    if (photoshootType === "two_person") {
      if (currentStep === 1) {
        // Check if person 1 has uploaded required photos
        if (uploadedFilesPerson1.length < MAX_FILES_UPLOAD) {
          return; // Don't proceed if not enough photos
        }
        // Move to person 2 upload
        setCurrentStep(2);
        return;
      }
      // For person 2, check if they have uploaded required photos
      if (uploadedFilesPerson2.length < MAX_FILES_UPLOAD) {
        return; // Don't proceed if not enough photos
      }
    } else {
      // Single person flow - check if enough photos uploaded
      // eslint-disable-next-line no-lonely-if
      if (uploadedFilesPerson1.length < MAX_FILES_UPLOAD) {
        return;
      }
    }

    // Proceed to job confirmation
    const payload = {
      key_features: photoshootType === "two_person" ? [confirmKeyFeature!, confirmKeyFeature2!] : [confirmKeyFeature!],
      styles: styleData.map(style => ({ background: style?.bg?.value, clothing: style?.clothing?.value as string })),
      headshot_styles: headshotStyleSelected,
      survey_ids: surveyIds,
    };

    // @ts-ignore
    const resAction = await dispatch(confirmJob({ data: payload!, id: jobId }));
    if (requestOK(resAction, confirmJob)) {
      dispatch(setDashboardProgress({ step: EDashboardStep.Download, screenIndex: 0 }));
      removeUserDashboardStorage();
      history.push(PAGES.COMPLETED);
    }
  };

  const getCurrentPersonName = () => {
    if (photoshootType === "one_person") {
      return confirmKeyFeature?.name || "your";
    }
    return currentStep === 1 ? confirmKeyFeature?.name || "Person 1" : confirmKeyFeature2?.name || "Person 2";
  };

  const getTitle = () => {
    if (photoshootType === "two_person") {
      return `Upload ${getCurrentPersonName()}'s photos`;
    }
    return "Upload Your Photos";
  };

  const getUploadText = () => {
    if (photoshootType === "two_person") {
      return `Upload ${MAX_FILES_UPLOAD} photos of ${getCurrentPersonName()}'s face`;
    }
    return `Upload ${MAX_FILES_UPLOAD} photos of your face`;
  };

  const getCurrentUploadedFiles = () => {
    if (photoshootType === "one_person") {
      return uploadedFilesPerson1;
    }
    return currentStep === 1 ? uploadedFilesPerson1 : uploadedFilesPerson2;
  };

  // Check if we can proceed to next step
  const canProceed = () => {
    if (photoshootType === "one_person") {
      return uploadedFilesPerson1.length === MAX_FILES_UPLOAD;
    }

    if (currentStep === 1) {
      return uploadedFilesPerson1.length === MAX_FILES_UPLOAD;
    }
    return uploadedFilesPerson1.length === MAX_FILES_UPLOAD && uploadedFilesPerson2.length === MAX_FILES_UPLOAD;
  };

  const getNextButtonText = () => {
    if (photoshootType === "two_person" && currentStep === 1) {
      return getCurrentUploadedFiles().length === MAX_FILES_UPLOAD ? "Continue to Person 2" : "Next";
    }
    return getCurrentUploadedFiles().length === MAX_FILES_UPLOAD ? "Upload Photos and Create Photoshoots" : "Next";
  };

  // Handler functions for Person 1
  const handlePerson1Success = (files: any[]) => {
    setUploadedFilesPerson1(prev => [...prev, ...files]);
  };

  const handlePerson1Remove = (file: any) => {
    setUploadedFilesPerson1(prev => prev.filter(f => f.id !== file.id && f.localUrl !== file.localUrl));
  };

  // Handler functions for Person 2
  const handlePerson2Success = (files: any[]) => {
    setUploadedFilesPerson2(prev => [...prev, ...files]);
  };

  const handlePerson2Remove = (file: any) => {
    setUploadedFilesPerson2(prev => prev.filter(f => f.id !== file.id && f.localUrl !== file.localUrl));
  };

  const currentUploadedFiles = getCurrentUploadedFiles();

  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot - Upload photos`,
        description: `Get 120 professional photoshoots in 3 hours using AI. Elevate your personal brand with corporate photoshoots.`,
        charset: "utf8",
        lang: "en",
        metas: [
          {
            name: "keywords",
            content: "corporate photoshoot,professional photoshoot,bettershot ai,linkedin profile photo,ai photoshoot",
          },
          { name: "robots", content: "noindex,nofollow" },
        ],
        links: [{ rel: "canonical", href: window.location.href }],
      }}
      as={Col}
      flex={1}
      isInGeneratingStep
    >
      <DisclaimerModal visible={showDisclaimerModal} onClose={() => setShowDisclaimerModal(false)} />
      <Col flex={1} justify="center">
        <UiCenter p={4} gap={5} flexDirection={{ base: "column-reverse", lg: "row" }}>
          <DashboardMainBox>
            <Col w="full" h="full">
              <UiBox w="full">
                <UiText vr={vrTitle} textAlign="center">
                  {getTitle()}
                </UiText>
                <UiText vr={vrCaption} mt={{ base: 2, sm: 3 }} textAlign="center">
                  Carefully read the photo
                  <UiText
                    as="span"
                    onClick={() => setShowDisclaimerModal(true)}
                    color="blue"
                    vr={vrCaption}
                    _hover={{ cursor: "pointer" }}
                  >
                    &nbsp;recommendations&nbsp;
                  </UiText>
                  to ensure that you get the best photoshoots.
                </UiText>
                <UiText vr={vrCaption} mt={2} textAlign="center">
                  Upload high quality images consisting almost entirely of {getCurrentPersonName()}&apos;s face.
                </UiText>

                {/* Show progress for two-person flow */}
                {photoshootType === "two_person" && (
                  <UiText vr="body3" mt={2} textAlign="center" color="neutral.gray600">
                    Step {currentStep} of 2 - {currentStep === 1 ? confirmKeyFeature?.name : confirmKeyFeature2?.name}
                  </UiText>
                )}
              </UiBox>

              <Col bg="neutral.gray00" rounded="2xl" mt={{ base: 4, md: 8 }} p={{ base: 4, md: 5 }} flex={1}>
                <UiFlex
                  flexDirection={{ base: "column", lg: "row" }}
                  justify="space-between"
                  align={{ base: undefined, sm: "center" }}
                  gap={1}
                >
                  <UiText vr="title1">{getUploadText()}</UiText>
                  <UiText>
                    You have uploaded
                    <UiBox as="span" color="blue" fontWeight="700">
                      &nbsp;{currentUploadedFiles?.length || 0}/{MAX_FILES_UPLOAD} photos
                    </UiBox>
                  </UiText>
                </UiFlex>
                <Col flex={1}>
                  {/* Show Person 1 DashboardDropzone when currentStep is 1 OR in one_person flow */}
                  {(photoshootType === "one_person" || currentStep === 1) && (
                    <DashboardDropzone
                      placeholderComponent={<PlaceholderUpload />}
                      maxFiles={MAX_FILES_UPLOAD}
                      files={uploadedFilesPerson1}
                      acceptType={["IMAGE", "ANDROID_IMAGE"]}
                      contentProps={{
                        borderStyle: "dashed",
                        borderWidth: "1px",
                        borderColor: "neutral.gray200",
                        rounded: "xl",
                        py: 10,
                        px: 1,
                        h: "100%",
                        bg: "neutral.gray00",
                      }}
                      type="first_person"
                      // onSuccess={handlePerson1Success}
                      // onRemove={handlePerson1Remove}
                    />
                  )}

                  {/* Show Person 2 DashboardDropzone when currentStep is 2 (only for two-person flow) */}
                  {photoshootType === "two_person" && currentStep === 2 && (
                    <DashboardDropzone
                      placeholderComponent={<PlaceholderUpload />}
                      maxFiles={MAX_FILES_UPLOAD}
                      files={uploadedFilesPerson2}
                      acceptType={["IMAGE", "ANDROID_IMAGE"]}
                      contentProps={{
                        borderStyle: "dashed",
                        borderWidth: "1px",
                        borderColor: "neutral.gray200",
                        rounded: "xl",
                        py: 10,
                        px: 1,
                        h: "100%",
                        bg: "neutral.gray00",
                      }}
                      type="second_person"
                      // onSuccess={handlePerson2Success}
                      // onRemove={handlePerson2Remove}
                    />
                  )}
                </Col>
              </Col>

              <Row
                gap={{ base: 2, sm: 5 }}
                flexDirection={{ base: "column", md: "row-reverse" }}
                align="center"
                justify="center"
                mt={8}
                w="full"
              >
                <UiPrimaryButton
                  w="full"
                  maxW={{
                    base: undefined,
                    md: canProceed() ? "fit-content" : "16.25rem",
                  }}
                  minW={{ md: "16.25rem" }}
                  isDisabled={currentUploadedFiles.length < MAX_FILES_UPLOAD || loading}
                  isLoading={confirmLoading}
                  onClick={onClickNext}
                >
                  {getNextButtonText()}
                </UiPrimaryButton>
                <UiPrimaryButton
                  w="full"
                  maxW={{ base: undefined, md: "16.25rem" }}
                  variant="outline"
                  borderColor="darkblue"
                  bg="transparent"
                  color="darkblue"
                  _hover={{
                    bg: "transparent",
                    color: "darkblue",
                  }}
                  onClick={onClickBack}
                >
                  <UiText vr="title1" color="darkblue">
                    Back
                  </UiText>
                </UiPrimaryButton>
              </Row>
            </Col>
          </DashboardMainBox>
          <DashboardStepper />
        </UiCenter>
      </Col>
    </PageLayout>
  );
}
